import React, {
	useCallback,
	useEffect,
	useLayoutEffect,
	useRef,
	useState,
} from "react";
import { classArr } from "../helper/style-helper";
import { useDebouncedEffect } from "../helper/hook-helper";
import * as styles from "./inline-text-area.module.scss";

const DEFAULT_DEBOUNCE_MS = 500;

function normalizeText(value: string | undefined): string {
	return value ?? "";
}

export interface InlineTextAreaProps {
	value: string | undefined;
	canEdit: boolean;
	onSave: (value: string) => Promise<void>;
	placeholder?: string;
	ariaLabel?: string;
	className?: string;
	debounceMs?: number;
}

/**
 * A borderless auto-resizing textarea that autosaves after a short pause.
 */
export default function InlineTextArea(props: InlineTextAreaProps) {
	const debounceMs = props.debounceMs ?? DEFAULT_DEBOUNCE_MS;

	const [draft, setDraft] = useState(() => normalizeText(props.value));
	const lastSavedValueRef = useRef(draft);
	const textareaRef = useRef<HTMLTextAreaElement>(null);

	//Sync the draft state with the value prop
	useEffect(() => {
		setDraft(normalizeText(props.value));
	}, [props.value]);

	const saveText = useCallback(
		(value: string) => {
			//Ignore if we are not editable
			if (!props.canEdit) {
				return;
			}

			//Ignore if the text has not changed
			if (value === lastSavedValueRef.current) {
				return;
			}

			//Save the text
			props.onSave(value).catch((error) => {
				console.warn("Failed to save text", error);
			});
			lastSavedValueRef.current = value;
		},
		[props.canEdit, props.onSave],
	);

	//Save the draft text after a debounce period
	useDebouncedEffect(
		() => {
			saveText(draft);
		},
		debounceMs,
		false,
		[draft, debounceMs, saveText],
	);

	//Measure the height of the textarea and set the height to the measured height
	useLayoutEffect(() => {
		const element = textareaRef.current;
		if (element === null) {
			return;
		}

		element.style.minHeight = "0px";
		element.style.height = "0px";
		const nextHeight = element.scrollHeight;
		element.style.minHeight = "";
		element.style.height = `${nextHeight}px`;
	}, [draft]);

	const handleBlur = useCallback(() => {
		saveText(draft);
	}, [draft, saveText]);

	const handleChange = useCallback(
		(event: React.ChangeEvent<HTMLTextAreaElement>) => {
			setDraft(event.target.value);
		},
		[],
	);

	if (props.canEdit) {
		return (
			<textarea
				ref={textareaRef}
				className={classArr(styles.input, props.className)}
				value={draft}
				placeholder={props.placeholder}
				aria-label={props.ariaLabel}
				rows={1}
				spellCheck
				onChange={handleChange}
				onBlur={handleBlur}
			/>
		);
	} else {
		//Return a read-only span if not editable
		return <>{props.value ?? props.placeholder}</>;
	}
}
